import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { useOrg } from '../../context/OrgContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Progress } from '../../components/ui/progress';
import EmployeeAvatar from '../../components/portal/EmployeeAvatar';
import HelpSectionHint from '../../components/portal/HelpSectionHint';
import API_BASE from '../../utils/apiBase';
import './dashboardSurface.css';
import {
  Users, AlertTriangle, FileX, Shield, ShieldCheck,
  FileCheck, CalendarClock, ArrowRight, Loader2,
  Clock, AlertCircle, CheckCircle, ClipboardList,
  ChevronDown, ChevronUp, Landmark, Briefcase, Mail, UserCheck,
} from 'lucide-react';

const API = API_BASE;

export default function DashboardPage() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [employees, setEmployees] = useState([]);
  const [applicants, setApplicants] = useState([]);
  const [expiryAlerts, setExpiryAlerts] = useState(null);
  const [recurringCompliance, setRecurringCompliance] = useState(null);
  const [showRecentEmployees, setShowRecentEmployees] = useState(false);
  const [outreachSummary, setOutreachSummary] = useState(null);
  const { token, user } = useAuth();
  const { orgName, orgSettings } = useOrg();
  const canUseSalesCrm = user?.workspace_entitlements?.features?.sales_crm === true;

  const tenantSetupItems = [
    {
      id: 'profile',
      label: 'Confirm organisation profile',
      detail: 'Legal name, address and registered manager details keep contracts and audits clean.',
      href: '/portal/settings',
      action: 'Open settings',
      done: Boolean(orgSettings?.company_name && orgSettings?.company_address && orgSettings?.registered_manager_name),
    },
    {
      id: 'pay',
      label: 'Set pay and invoice defaults',
      detail: 'HCA/Nurse pay defaults, payment terms and bank details prevent payroll and invoices from stalling later.',
      href: '/portal/settings',
      action: 'Complete defaults',
      done: Boolean(
        orgSettings?.default_hca_hourly_rate
        && orgSettings?.default_nurse_hourly_rate
        && orgSettings?.invoice_payment_terms
        && orgSettings?.invoice_bank_details
      ),
    },
    {
      id: 'recruitment',
      label: 'Invite applicants privately',
      detail: 'Use Recruitment to send tenant-specific applicant links. Do not use a public open application link.',
      href: '/portal/recruitment',
      action: 'Open recruitment',
      done: Boolean((stats?.total_applicants || 0) > 0 || employees.length > 0),
    },
  ];
  const tenantSetupDone = tenantSetupItems.filter((item) => item.done).length;
  const showTenantSetup = tenantSetupDone < tenantSetupItems.length;

  const renderTenantSetupChecklist = () => (
    <Card className="border-[#D7E6E4] shadow-sm bg-white" data-testid="tenant-setup-checklist">
      <CardHeader>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <ClipboardList className="h-5 w-5 text-primary" />
              Workspace setup
            </CardTitle>
            <p className="text-sm text-text-muted mt-1">
              Finish these first so your tenant is ready for staff onboarding, documents and billing.
            </p>
          </div>
          <span className="text-sm font-medium text-primary">{tenantSetupDone}/{tenantSetupItems.length} complete</span>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid gap-3 lg:grid-cols-2">
          {tenantSetupItems.map((item) => (
            <div
              key={item.id}
              className={`rounded-lg border p-4 ${
                item.done ? 'border-emerald-200 bg-emerald-50/60' : 'border-amber-200 bg-amber-50/50'
              }`}
            >
              <div className="flex items-start gap-3">
                <div className={`mt-0.5 h-7 w-7 rounded-lg flex items-center justify-center ${
                  item.done ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
                }`}>
                  {item.done ? <CheckCircle className="h-4 w-4" /> : <Clock className="h-4 w-4" />}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-text-primary">{item.label}</p>
                  <p className="text-sm text-text-muted mt-1">{item.detail}</p>
                  {!item.done && (
                    <Link to={item.href} className="inline-flex mt-3">
                      <Button size="sm" variant="outline" className="h-8 rounded-lg">
                        {item.action}
                        <ArrowRight className="ml-2 h-3.5 w-3.5" />
                      </Button>
                    </Link>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );

  useEffect(() => {
    let cancelled = false;
    const headers = { Authorization: `Bearer ${token}` };

    const fetchData = async () => {
      // Secondary dashboard detail should never hold the whole page behind a spinner.
      // Start it immediately, then let it populate as soon as each summary is ready.
      const secondaryPromise = Promise.allSettled([
        axios.get(`${API}/dashboard/expiry-alerts`, { headers }),
        axios.get(`${API}/recurring-compliance/dashboard-summary`, { headers }),
        canUseSalesCrm
          ? axios.get(`${API}/sales-leads/summary`, { headers }).catch(() => ({ data: null }))
          : Promise.resolve({ data: null }),
      ]).then((results) => {
        if (cancelled) return;
        const [expiryRes, recurringRes, outreachRes] = results.map((r) => (
          r.status === 'fulfilled' ? r.value : { data: null }
        ));
        setExpiryAlerts(expiryRes.data);
        setRecurringCompliance(recurringRes.data);
        setOutreachSummary(outreachRes.data || null);
      });

      try {
        const results = await Promise.allSettled([
          axios.get(`${API}/dashboard/stats`, { headers }),
          axios.get(`${API}/staff/employees`, { headers }),
        ]);
        if (cancelled) return;

        const [statsRes, employeesRes] = results.map((r) => (
          r.status === 'fulfilled' ? r.value : { data: null }
        ));
        setStats(statsRes.data || {});

        // Applicants remain in Recruitment. The dashboard only needs live staff.
        const staffOnly = (employeesRes.data?.employees || employeesRes.data || [])
          .filter((employee) => ['onboarding', 'active', 'inactive'].includes(employee.status));
        const employeeIds = staffOnly.map((employee) => employee.id).filter(Boolean);

        // Canonical readiness is the only additional call that gates the initial
        // operational counts. It runs as soon as the staff list arrives.
        const readinessRes = employeeIds.length > 0
          ? await axios.get(`${API}/employees/unified-progress-summary`, {
              params: { employee_ids: employeeIds.join(',') },
              headers,
            }).catch(() => ({ data: [] }))
          : { data: [] };
        if (cancelled) return;

        const readinessByEmployeeId = new Map(
          (readinessRes.data || []).map((summary) => [summary.employee_id, summary])
        );
        setEmployees(staffOnly.map((employee) => ({
          ...employee,
          canonical_readiness: readinessByEmployeeId.get(employee.id) || null,
        })));
        setApplicants([]);
      } catch (error) {
        console.error('Failed to fetch dashboard data:', error);
      } finally {
        if (!cancelled) setLoading(false);
      }

      // Keep the promise alive for error handling/state updates without making
      // the initial dashboard wait for these secondary summaries.
      void secondaryPromise;
    };

    fetchData();
    return () => {
      cancelled = true;
    };
  }, [token, canUseSalesCrm]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // ========== EMPTY STATE: Only show if NO employees AND NO applicants ==========
  const totalPeople = employees.length + applicants.length;
  
  if (totalPeople === 0) {
    return (
      <div className="space-y-6 caredock-ops-dashboard" data-testid="dashboard-page-empty">
        {/* Header */}
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-text-primary">
            Welcome to {orgName} Compliance Portal
          </h1>
          <p className="text-text-muted mt-1">
            Get started by inviting your first applicant through the private recruitment flow.
          </p>
        </div>

        {/* Get Started Card */}
        <Card className="border-2 border-primary/20 bg-gradient-to-br from-primary/5 to-white shadow-lg">
          <CardContent className="pt-8 pb-8">
            <div className="text-center max-w-md mx-auto">
              <div className="w-20 h-20 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <Users className="h-10 w-10 text-primary" />
              </div>
              
              <h2 className="text-xl font-heading font-bold text-text-primary mb-2">
                No Staff Yet
              </h2>
              <p className="text-text-muted mb-8">
                Invite applicants privately first. Approved applicants move into Staff after recruitment checks are complete.
              </p>

              <div className="space-y-4">
                <Link to="/portal/recruitment" className="block">
                  <Button className="w-full bg-primary hover:bg-primary-hover text-white rounded-xl py-6 text-lg" data-testid="get-started-add-employee">
                    <UserCheck className="mr-2 h-5 w-5" />
                    Invite First Applicant
                  </Button>
                </Link>
              </div>
            </div>
          </CardContent>
        </Card>

        {renderTenantSetupChecklist()}
      </div>
    );
  }

  // ========== NORMAL DASHBOARD (employees/applicants exist) ==========
  
  // Calculate workforce readiness counts from canonical unified-progress.
  // This is the single source of truth for "safe to deploy" (combines DBS, training, RTW, policy acks, blocks).
  // Green = legally safe to put on shift. Amber/red = do not roster until resolved (CQC safe staffing requirement).
  const readyToWork = employees.filter(e => 
    e.canonical_readiness?.is_work_ready === true
  ).length;
  const supervisedStart = employees.filter(e => 
    e.canonical_readiness?.can_promote === true && e.canonical_readiness?.is_work_ready !== true
  ).length;
  
  // Not Ready includes employees without canonical readiness.
  const employeesNotReady = employees.filter(e => 
    e.canonical_readiness?.is_work_ready !== true &&
    e.canonical_readiness?.can_promote !== true
  ).length;
  const notReady = employeesNotReady;

  // Count employees in onboarding stage only — applicants are a separate population
  const onboarding = employees.filter(e => e.status === 'onboarding').length;
  const activeStaff = employees.filter(e => e.status === 'active').length;
  
  // Average readiness progress from canonical unified-progress for employees.
  const employeesWithCanonicalProgress = employees.filter(e => e.canonical_readiness);
  const avgCompletion = employeesWithCanonicalProgress.length > 0
    ? Math.round(
        employeesWithCanonicalProgress.reduce(
          (sum, e) => sum + (e.canonical_readiness?.overall_percentage ?? 0),
          0
        ) / employeesWithCanonicalProgress.length
      )
    : (stats?.average_onboarding_progress || 0);

  // Financial pipeline truth note (tied to our recent badges + E2E work).
  // "Financially Closed" = approved attendance on locked timesheet + payroll processed + client invoiced.
  // The new badges on Shifts page and Worker "My Earnings" now surface this accurately.
  // Dashboard should reflect high-level health of this engine (e.g., awaiting timesheet, ready for payroll/invoice).

  // Calculate attention items from expiry alerts API
  const classifyExpiryItem = (item) => {
    const req = String(item?.requirement_id || '').toLowerCase();
    const name = String(item?.name || '').toLowerCase();
    if (req.includes('training') || name.includes('training')) return 'training';
    return 'document';
  };
  const expiredEmployees = expiryAlerts?.expired?.employees || [];
  const expiringEmployees = expiryAlerts?.expiring_soon?.employees || [];
  let expiredTraining = 0;
  let expiredDocuments = 0;
  let expiringTraining = 0;
  let expiringDocuments = 0;
  expiredEmployees.forEach((emp) => {
    (emp.expired_items || []).forEach((item) => {
      if (classifyExpiryItem(item) === 'training') expiredTraining += 1;
      else expiredDocuments += 1;
    });
  });
  expiringEmployees.forEach((emp) => {
    (emp.expiring_items || []).forEach((item) => {
      if (classifyExpiryItem(item) === 'training') expiringTraining += 1;
      else expiringDocuments += 1;
    });
  });
  const expiredDocs = expiryAlerts?.expired?.total_items || 0;
  const expiringSoon = expiryAlerts?.expiring_soon?.total_items || stats?.expiring_30_days || 0;
  const policiesNotAcknowledged = stats?.unsigned_policies || 0;
  
  // Pending verifications from stats (includes applicants)
  const pendingVerifications = stats?.pending_verifications || 0;
  
  // Calculate staff not ready to work from canonical employee readiness only.
  const staffNotReady = employeesNotReady;
  
  // Match the Needs Attention cards below: employee issues plus the dedicated recruitment pipeline.
  const employeeNeedsAttention = expiredDocs + expiringSoon + policiesNotAcknowledged + staffNotReady;
  const recruitmentPipelineCount = stats?.total_applicants ?? pendingVerifications;
  const needsAttentionTotal = employeeNeedsAttention + recruitmentPipelineCount;
  const riskGroups = [
    { id: 'expired', label: 'Expired evidence', tone: 'red', href: '/portal/compliance-centre?tab=staff', matches: ['expired'] },
    { id: 'dbs', label: 'DBS', tone: 'red', href: '/portal/dbs-register', matches: ['dbs'] },
    { id: 'right_to_work', label: 'Right to Work', tone: 'red', href: '/portal/compliance-centre?tab=staff', matches: ['right to work', 'rtw'] },
    { id: 'training', label: 'Training', tone: 'amber', href: '/portal/training', matches: ['training', 'mandatory training'] },
    { id: 'identity', label: 'Identity', tone: 'amber', href: '/portal/compliance-centre?tab=staff', matches: ['identity'] },
    { id: 'proof_of_address', label: 'Proof of address', tone: 'amber', href: '/portal/compliance-centre?tab=staff', matches: ['proof of address', 'address'] },
    { id: 'nmc', label: 'NMC', tone: 'red', href: '/portal/compliance-centre?tab=staff', matches: ['nmc', 'professional registration'] },
    { id: 'other', label: 'Other blockers', tone: 'slate', href: '/portal/employees?is_work_ready=false', matches: [] },
  ];
  const readinessRiskText = (blocker) => [
      blocker?.id,
      blocker?.requirement_id,
      blocker?.label,
      blocker?.reason,
      blocker?.category,
      blocker?.verification_status,
    ].map((value) => String(value || '').toLowerCase()).join(' ');
  const riskMatchesGroup = (text, group) => group.matches.some((match) => text.includes(match));
  const classifyReadinessRisk = (blocker) => {
    const text = readinessRiskText(blocker);
    return riskGroups.find((group) => group.id !== 'other' && group.matches.some((match) => text.includes(match))) || riskGroups[riskGroups.length - 1];
  };
  const complianceRiskRows = [];
  employees.forEach((employee) => {
    if (employee.status !== 'active') return;
    const readiness = employee.canonical_readiness || {};
    const details = Array.isArray(readiness.blocker_details) && readiness.blocker_details.length > 0
      ? readiness.blocker_details
      : (readiness.blockers || []).map((reason) => ({ reason }));
    if (readiness.is_work_ready === true || details.length === 0) return;
    details.forEach((blocker) => {
      const text = readinessRiskText(blocker);
      const group = classifyReadinessRisk(blocker);
      complianceRiskRows.push({
        employee,
        text,
        groupId: group.id,
        groupLabel: group.label,
        reason: blocker?.reason || blocker?.label || 'Work-readiness blocker',
        severity: String(blocker?.severity || '').toLowerCase() || 'pending',
      });
    });
  });
  const complianceRiskCounts = riskGroups.map((group) => ({
    ...group,
    count: group.id === 'other'
      ? complianceRiskRows.filter((row) => !riskGroups.some((candidate) => candidate.id !== 'other' && riskMatchesGroup(row.text, candidate))).length
      : complianceRiskRows.filter((row) => riskMatchesGroup(row.text, group)).length,
  }));
  const totalComplianceRisks = complianceRiskRows.length;
  const topComplianceRiskRows = complianceRiskRows.slice(0, 4);
  const riskToneClass = (tone, active = true) => {
    if (!active) return 'border-[#E4E8EB] bg-white text-slate-500';
    if (tone === 'red') return 'border-red-200 bg-red-50 text-red-800';
    if (tone === 'amber') return 'border-amber-200 bg-amber-50 text-amber-800';
    return 'border-slate-200 bg-slate-50 text-slate-700';
  };


  return (
    <div className="space-y-6 caredock-ops-dashboard" data-testid="dashboard-page">
      {/* Header with microcopy */}
      <div data-help-section="header">
        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-text-primary flex items-center gap-2">
          {orgName} — Overview
          <HelpSectionHint sectionId="header" />
        </h1>
        <p className="text-text-muted mt-1">
          See what needs attention, then open the right workspace. Everything else stays in its own section.
        </p>
      </div>

      {showTenantSetup && renderTenantSetupChecklist()}


      {canUseSalesCrm && outreachSummary && (
        <Card className="border-[#E4E8EB] shadow-sm" data-testid="outreach-summary-strip">
          <CardHeader className="pb-3">
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <Landmark className="h-5 w-5 text-primary" />
              Sales CRM
            </CardTitle>
            <p className="text-sm text-text-muted">
              Care home leads to chase, follow-ups due, staffing requests, and website messages
            </p>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <button
                type="button"
                onClick={() => navigate('/portal/sales-leads')}
                className="rounded-xl border p-3 text-left bg-white border-[#E4E8EB] hover:bg-[#F8FAFA] transition-colors"
              >
                <p className="text-xs text-text-muted">Open leads</p>
                <p className="mt-1 text-2xl font-heading font-bold text-text-primary">{outreachSummary.open_leads ?? outreachSummary.prospects ?? 0}</p>
              </button>
              <button
                type="button"
                onClick={() => navigate('/portal/sales-leads?follow_up_due=1')}
                className={`rounded-xl border p-3 text-left transition-colors ${
                  (outreachSummary.follow_up_due ?? 0) > 0 ? 'bg-red-50 border-red-200 hover:bg-red-100' : 'bg-white border-[#E4E8EB] hover:bg-[#F8FAFA]'
                }`}
              >
                <p className={`text-xs ${(outreachSummary.follow_up_due ?? 0) > 0 ? 'text-red-700' : 'text-text-muted'}`}>Follow-up due</p>
                <p className={`mt-1 text-2xl font-heading font-bold ${(outreachSummary.follow_up_due ?? 0) > 0 ? 'text-red-700' : 'text-text-primary'}`}>
                  {outreachSummary.follow_up_due ?? 0}
                </p>
              </button>
              <button
                type="button"
                onClick={() => navigate('/portal/staffing-requests')}
                className="rounded-xl border p-3 text-left bg-white border-[#E4E8EB] hover:bg-[#F8FAFA] transition-colors"
              >
                <p className="text-xs text-text-muted flex items-center gap-1"><Briefcase className="h-3 w-3" />Open staffing</p>
                <p className="mt-1 text-2xl font-heading font-bold text-text-primary">{outreachSummary.open_staffing_requests ?? 0}</p>
              </button>
              <button
                type="button"
                onClick={() => navigate('/portal/contact-submissions')}
                className={`rounded-xl border p-3 text-left transition-colors ${
                  (outreachSummary.new_contact_submissions ?? 0) > 0 ? 'bg-blue-50 border-blue-200 hover:bg-blue-100' : 'bg-white border-[#E4E8EB] hover:bg-[#F8FAFA]'
                }`}
              >
                <p className="text-xs text-text-muted flex items-center gap-1"><Mail className="h-3 w-3" />New messages</p>
                <p className="mt-1 text-2xl font-heading font-bold text-text-primary">{outreachSummary.new_contact_submissions ?? 0}</p>
              </button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* PRIMARY: single action centre */}
      <Card className={`border-2 ${needsAttentionTotal > 0 ? 'border-red-200 bg-red-50' : 'border-green-200 bg-green-50'} shadow-sm`} data-help-section="needs-attention" data-testid="action-centre">
        <CardHeader className="pb-3">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                Action Centre
                <HelpSectionHint sectionId="needs-attention" />
              </CardTitle>
              <p className="text-sm text-text-muted mt-1">
                Open a card to review the records needing attention.
              </p>
            </div>
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              {needsAttentionTotal > 0 ? (
                <>
                  <AlertTriangle className="h-5 w-5 text-red-600" />
                  <span className="text-red-700">{needsAttentionTotal} flagged</span>
                </>
              ) : (
                <>
                  <CheckCircle className="h-5 w-5 text-green-600" />
                  <span className="text-green-700">No dashboard blockers</span>
                </>
              )}
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          {needsAttentionTotal > 0 ? (
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Expired — split training vs compliance documents */}
              <div 
                className={`p-4 rounded-xl transition-all ${expiredDocs > 0 ? 'bg-red-100 border border-red-200' : 'bg-white border border-gray-200'}`}
                data-testid="card-expired"
              >
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${expiredDocs > 0 ? 'bg-red-200' : 'bg-gray-100'}`}>
                    <FileX className={`h-5 w-5 ${expiredDocs > 0 ? 'text-red-600' : 'text-gray-400'}`} />
                  </div>
                  <div className="flex-1">
                    <p className={`text-2xl font-heading font-bold ${expiredDocs > 0 ? 'text-red-700' : 'text-gray-400'}`}>{expiredDocs}</p>
                    <p className={`text-sm ${expiredDocs > 0 ? 'text-red-600' : 'text-gray-500'}`}>Expired items</p>
                  </div>
                </div>
                {expiredDocs > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {expiredTraining > 0 && (
                      <button
                        type="button"
                        onClick={() => navigate('/portal/training?filter=expired')}
                        className="text-xs rounded-lg bg-white px-2.5 py-1.5 text-red-700 border border-red-200 hover:bg-red-50"
                      >
                        Training ({expiredTraining}) →
                      </button>
                    )}
                    {expiredDocuments > 0 && (
                      <button
                        type="button"
                        onClick={() => navigate('/portal/compliance-centre?tab=staff')}
                        className="text-xs rounded-lg bg-white px-2.5 py-1.5 text-red-700 border border-red-200 hover:bg-red-50"
                      >
                        Documents ({expiredDocuments}) →
                      </button>
                    )}
                  </div>
                )}
              </div>
              
              {/* Expiring soon — split training vs documents */}
              <div 
                className={`p-4 rounded-xl transition-all ${expiringSoon > 0 ? 'bg-amber-100 border border-amber-200' : 'bg-white border border-gray-200'}`}
                data-testid="card-expiring"
              >
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${expiringSoon > 0 ? 'bg-amber-200' : 'bg-gray-100'}`}>
                    <CalendarClock className={`h-5 w-5 ${expiringSoon > 0 ? 'text-amber-600' : 'text-gray-400'}`} />
                  </div>
                  <div className="flex-1">
                    <p className={`text-2xl font-heading font-bold ${expiringSoon > 0 ? 'text-amber-700' : 'text-gray-400'}`}>{expiringSoon}</p>
                    <p className={`text-sm ${expiringSoon > 0 ? 'text-amber-600' : 'text-gray-500'}`}>Renewals due soon</p>
                  </div>
                </div>
                {expiringSoon > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {expiringTraining > 0 && (
                      <button
                        type="button"
                        onClick={() => navigate('/portal/training?filter=expiring_soon')}
                        className="text-xs rounded-lg bg-white px-2.5 py-1.5 text-amber-800 border border-amber-200 hover:bg-amber-50"
                      >
                        Training ({expiringTraining}) →
                      </button>
                    )}
                    {expiringDocuments > 0 && (
                      <button
                        type="button"
                        onClick={() => navigate('/portal/compliance-centre?tab=staff')}
                        className="text-xs rounded-lg bg-white px-2.5 py-1.5 text-amber-800 border border-amber-200 hover:bg-amber-50"
                      >
                        Documents ({expiringDocuments}) →
                      </button>
                    )}
                  </div>
                )}
              </div>
              
              {/* Staff Not Ready → Employees filtered to not_ready */}
              <button type="button" 
                disabled={staffNotReady === 0}
                onClick={() => staffNotReady > 0 && navigate('/portal/employees?is_work_ready=false')}
                className={`w-full text-left p-4 rounded-xl transition-all ${staffNotReady > 0 ? 'bg-red-100 border border-red-200 cursor-pointer hover:bg-red-150 hover:shadow-md' : 'bg-white border border-gray-200'}`}
                title={staffNotReady > 0 ? 'View affected staff' : ''}
                data-testid="card-not-ready"
              >
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${staffNotReady > 0 ? 'bg-red-200' : 'bg-gray-100'}`}>
                    <AlertTriangle className={`h-5 w-5 ${staffNotReady > 0 ? 'text-red-600' : 'text-gray-400'}`} />
                  </div>
                  <div className="flex-1">
                    <p className={`text-2xl font-heading font-bold ${staffNotReady > 0 ? 'text-red-700' : 'text-gray-400'}`}>{staffNotReady}</p>
                    <p className={`text-sm ${staffNotReady > 0 ? 'text-red-600' : 'text-gray-500'}`}>Not Ready to Work</p>
                  </div>
                  {staffNotReady > 0 && <ArrowRight className="h-4 w-4 text-red-400" />}
                </div>
                {staffNotReady > 0 && <p className="text-xs text-red-500 mt-2">Open details →</p>}
              </button>
              
              {/* Dedicated Recruitment Pipeline — separate from active staff (fast Dashboard, no heavy applicant data loaded here) */}
              <button type="button" 
                onClick={() => navigate('/portal/recruitment')}
                className={`w-full text-left p-4 rounded-xl transition-all ${recruitmentPipelineCount > 0 ? 'bg-purple-100 border-2 border-purple-300 cursor-pointer hover:bg-purple-150 hover:shadow-md' : 'bg-white border border-gray-200'}`}
                title="Open dedicated Recruitment pipeline"
                data-testid="card-recruitment-pipeline"
              >
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${recruitmentPipelineCount > 0 ? 'bg-purple-200' : 'bg-gray-100'}`}>
                    <UserCheck className={`h-5 w-5 ${recruitmentPipelineCount > 0 ? 'text-purple-600' : 'text-gray-400'}`} />
                  </div>
                  <div className="flex-1">
                    <p className={`text-2xl font-heading font-bold ${recruitmentPipelineCount > 0 ? 'text-purple-700' : 'text-gray-400'}`}>{recruitmentPipelineCount}</p>
                    <p className={`text-sm ${recruitmentPipelineCount > 0 ? 'text-purple-600' : 'text-gray-500'}`}>In Recruitment Pipeline</p>
                  </div>
                  <ArrowRight className="h-4 w-4 text-purple-400" />
                </div>
                <p className="text-xs text-purple-700 mt-2 font-medium">
                  {pendingVerifications > 0 ? `${pendingVerifications} documents awaiting review` : 'Open recruitment to review applicants'}
                </p>
              </button>
              
              {/* Policies Not Acknowledged → Compliance Centre policies tab */}
              <button type="button" 
                disabled={policiesNotAcknowledged === 0}
                onClick={() => policiesNotAcknowledged > 0 && navigate('/portal/compliance-centre?tab=policies')}
                className={`w-full text-left p-4 rounded-xl transition-all ${policiesNotAcknowledged > 0 ? 'bg-blue-100 border border-blue-200 cursor-pointer hover:bg-blue-150 hover:shadow-md' : 'bg-white border border-gray-200'}`}
                title={policiesNotAcknowledged > 0 ? 'Review policy acknowledgements' : ''}
                data-testid="card-policies"
              >
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${policiesNotAcknowledged > 0 ? 'bg-blue-200' : 'bg-gray-100'}`}>
                    <FileCheck className={`h-5 w-5 ${policiesNotAcknowledged > 0 ? 'text-blue-600' : 'text-gray-400'}`} />
                  </div>
                  <div className="flex-1">
                    <p className={`text-2xl font-heading font-bold ${policiesNotAcknowledged > 0 ? 'text-blue-700' : 'text-gray-400'}`}>{policiesNotAcknowledged}</p>
                    <p className={`text-sm ${policiesNotAcknowledged > 0 ? 'text-blue-600' : 'text-gray-500'}`}>Policies Not Yet Acknowledged</p>
                  </div>
                  {policiesNotAcknowledged > 0 && <ArrowRight className="h-4 w-4 text-blue-400" />}
                </div>
                {policiesNotAcknowledged > 0 && <p className="text-xs text-blue-500 mt-2">Open details →</p>}
              </button>
              </div>
            </div>
          ) : (
            <div className="rounded-xl border border-green-200 bg-white px-4 py-5 text-green-800">
              <p className="font-semibold">No urgent Action Centre items right now.</p>
              <p className="text-sm mt-1">
                Routine renewals, training due soon, recruitment progress and finance work stay visible in their own panels when they exist.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Drill-down only: Action Centre is the single daily queue. */}
      {totalComplianceRisks > 0 && (
      <Card className="border-2 border-red-200 bg-white shadow-sm" data-testid="admin-compliance-risk-panel">
        <CardHeader className="pb-3">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <ShieldCheck className="h-5 w-5 text-primary" />
                Ongoing Compliance Risk
              </CardTitle>
              <p className="text-sm text-text-muted mt-1">
                Active staff readiness grouped by the reason they cannot safely be booked for work.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant={totalComplianceRisks > 0 ? 'default' : 'outline'}
                className={totalComplianceRisks > 0 ? 'bg-red-700 hover:bg-red-800 text-white rounded-xl' : 'rounded-xl'}
                onClick={() => navigate('/portal/employees?is_work_ready=false')}
                data-testid="open-blocked-workers"
              >
                Blocked workers
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <Button
                type="button"
                variant="outline"
                className="rounded-xl"
                onClick={() => navigate('/portal/compliance-centre?tab=staff')}
                data-testid="open-compliance-centre"
              >
                Review evidence
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <details className="rounded-xl border border-[#E4E8EB] bg-white" data-testid="compliance-risk-details">
              <summary className="cursor-pointer list-none px-4 py-3 text-sm font-semibold text-text-primary flex items-center justify-between gap-3">
                <span>{totalComplianceRisks} active blocker{totalComplianceRisks === 1 ? '' : 's'} — show categories and affected staff</span>
                <ChevronDown className="h-4 w-4 text-slate-500" />
              </summary>
              <div className="border-t border-[#E4E8EB] p-4">

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {complianceRiskCounts.map((group) => (
              <button
                key={group.id}
                type="button"
                onClick={() => group.count > 0 && navigate(group.href)}
                className={`rounded-xl border p-3 text-left transition-colors ${riskToneClass(group.tone, group.count > 0)} ${group.count > 0 ? 'hover:shadow-sm' : ''}`}
                data-testid={`compliance-risk-${group.id}`}
              >
                <p className="text-xs font-medium">{group.label}</p>
                <p className="mt-1 text-2xl font-heading font-bold">{group.count}</p>
              </button>
            ))}
          </div>

            <div className="mt-4 space-y-2">
              <div className="flex items-center justify-between gap-3">
                <p className="text-sm font-medium text-text-muted">Top affected workers</p>
                <span className="text-xs rounded-full border border-red-200 bg-red-50 px-2.5 py-1 text-red-700">
                  {totalComplianceRisks} blocker{totalComplianceRisks === 1 ? '' : 's'}
                </span>
              </div>
              {topComplianceRiskRows.map((row, index) => (
                <Link
                  key={`${row.employee.id}-${row.groupId}-${index}`}
                  to={`/portal/employees/${row.employee.id}`}
                  className="flex flex-col gap-2 rounded-xl border border-[#E4E8EB] bg-white p-3 hover:bg-[#F8FAFA] sm:flex-row sm:items-center sm:justify-between"
                  data-testid={`compliance-risk-worker-${index}`}
                >
                  <div className="min-w-0">
                    <p className="font-medium text-text-primary">
                      {row.employee.first_name} {row.employee.last_name}
                    </p>
                    <p className="text-sm text-text-muted truncate">{row.reason}</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className={`rounded-full border px-2.5 py-1 text-xs ${row.severity === 'critical' ? 'border-red-200 bg-red-50 text-red-700' : 'border-amber-200 bg-amber-50 text-amber-700'}`}>
                      {row.groupLabel}
                    </span>
                    <ArrowRight className="h-4 w-4 text-slate-400" />
                  </div>
                </Link>
              ))}
            </div>

              </div>
            </details>
        </CardContent>
      </Card>
      )}

      {/* SECONDARY: Workforce Readiness + Onboarding Progress */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Workforce Readiness */}
        <Card className="border-[#E4E8EB] shadow-sm">
          <CardHeader>
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <Shield className="h-5 w-5 text-primary" />
              Workforce Readiness
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {/* Ready to Work → Employees filtered to ready_to_work */}
              <div 
                onClick={() => readyToWork > 0 && navigate('/portal/employees?is_work_ready=true')}
                className={`flex items-center justify-between p-3 bg-green-50 rounded-xl border border-green-200 transition-all ${readyToWork > 0 ? 'cursor-pointer hover:bg-green-100 hover:shadow-sm' : ''}`}
                title={readyToWork > 0 ? 'View ready staff' : ''}
                data-testid="card-ready-to-work"
              >
                <div className="flex items-center gap-3">
                  <ShieldCheck className="h-5 w-5 text-green-600" />
                  <span className="font-medium text-green-700">Ready to Work</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-2xl font-heading font-bold text-green-700">{readyToWork}</span>
                  {readyToWork > 0 && <ArrowRight className="h-4 w-4 text-green-400" />}
                </div>
              </div>
              
              {/* Supervised Start → Employees filtered to supervised_start */}
              <div 
                onClick={() => supervisedStart > 0 && navigate('/portal/employees?can_promote=true')}
                className={`flex items-center justify-between p-3 bg-amber-50 rounded-xl border border-amber-200 transition-all ${supervisedStart > 0 ? 'cursor-pointer hover:bg-amber-100 hover:shadow-sm' : ''}`}
                title={supervisedStart > 0 ? 'View staff eligible for promotion' : ''}
                data-testid="card-supervised-start"
              >
                <div className="flex items-center gap-3">
                  <AlertCircle className="h-5 w-5 text-amber-600" />
                  <span className="font-medium text-amber-700">Promotion Eligible</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-2xl font-heading font-bold text-amber-700">{supervisedStart}</span>
                  {supervisedStart > 0 && <ArrowRight className="h-4 w-4 text-amber-400" />}
                </div>
              </div>
              
              {/* Not Ready → Employees filtered to not_ready */}
              <div 
                onClick={() => notReady > 0 && navigate('/portal/employees?is_work_ready=false')}
                className={`flex items-center justify-between p-3 bg-red-50 rounded-xl border border-red-200 transition-all ${notReady > 0 ? 'cursor-pointer hover:bg-red-100 hover:shadow-sm' : ''}`}
                title={notReady > 0 ? 'View affected staff' : ''}
                data-testid="card-not-ready-workforce"
              >
                <div className="flex items-center gap-3">
                  <AlertTriangle className="h-5 w-5 text-red-600" />
                  <span className="font-medium text-red-700">Not Ready</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-2xl font-heading font-bold text-red-700">{notReady}</span>
                  {notReady > 0 && <ArrowRight className="h-4 w-4 text-red-400" />}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Onboarding Progress */}
        <Card className="border-[#E4E8EB] shadow-sm">
          <CardHeader>
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <Clock className="h-5 w-5 text-primary" />
              Onboarding Progress
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-blue-50 rounded-xl border border-blue-200">
                <div className="flex items-center gap-3">
                  <Users className="h-5 w-5 text-blue-600" />
                  <span className="font-medium text-blue-700">In Progress</span>
                </div>
                <span className="text-2xl font-heading font-bold text-blue-700">{onboarding}</span>
              </div>
              <div className="p-4 bg-[#F8FAFA] rounded-xl border border-[#E4E8EB]">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-text-muted">Average Employee Compliance</span>
                  <span className="font-semibold text-text-primary">{avgCompletion}%</span>
                </div>
                <Progress value={avgCompletion} className="h-3" />
              </div>
              <div className="flex justify-between items-center p-3 bg-[#F8FAFA] rounded-xl border border-[#E4E8EB]">
                <span className="text-text-muted">Active Staff (Employees)</span>
                <span className="text-xl font-heading font-bold text-text-primary">{activeStaff}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recurring Compliance Summary */}
      {recurringCompliance && (recurringCompliance.summary?.overdue > 0 || recurringCompliance.summary?.due > 0 || recurringCompliance.summary?.upcoming > 0) && (
        <Card className="border-[#E4E8EB] shadow-sm">
          <CardHeader>
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <ClipboardList className="h-5 w-5 text-primary" />
              Recurring Compliance
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
              {/* Overdue */}
              <div className={`p-4 rounded-xl border ${recurringCompliance.summary?.overdue > 0 ? 'bg-red-50 border-red-200' : 'bg-white border-[#E4E8EB]'}`}>
                <div className="flex items-center justify-between">
                  <span className={`text-sm font-medium ${recurringCompliance.summary?.overdue > 0 ? 'text-red-700' : 'text-gray-500'}`}>Overdue</span>
                  <AlertTriangle className={`h-4 w-4 ${recurringCompliance.summary?.overdue > 0 ? 'text-red-600' : 'text-gray-400'}`} />
                </div>
                <p className={`text-2xl font-heading font-bold mt-1 ${recurringCompliance.summary?.overdue > 0 ? 'text-red-700' : 'text-gray-400'}`}>
                  {recurringCompliance.summary?.overdue || 0}
                </p>
              </div>
              
              {/* Due */}
              <div className={`p-4 rounded-xl border ${recurringCompliance.summary?.due > 0 ? 'bg-amber-50 border-amber-200' : 'bg-white border-[#E4E8EB]'}`}>
                <div className="flex items-center justify-between">
                  <span className={`text-sm font-medium ${recurringCompliance.summary?.due > 0 ? 'text-amber-700' : 'text-gray-500'}`}>Due Now</span>
                  <Clock className={`h-4 w-4 ${recurringCompliance.summary?.due > 0 ? 'text-amber-600' : 'text-gray-400'}`} />
                </div>
                <p className={`text-2xl font-heading font-bold mt-1 ${recurringCompliance.summary?.due > 0 ? 'text-amber-700' : 'text-gray-400'}`}>
                  {recurringCompliance.summary?.due || 0}
                </p>
              </div>
              
              {/* Upcoming */}
              <div className="p-4 rounded-xl border bg-white border-[#E4E8EB]">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-blue-700">Upcoming</span>
                  <CalendarClock className="h-4 w-4 text-blue-600" />
                </div>
                <p className="text-2xl font-heading font-bold mt-1 text-blue-700">
                  {recurringCompliance.summary?.upcoming || 0}
                </p>
              </div>
            </div>

            {/* Urgent Items List */}
            {(recurringCompliance.overdue_items?.length > 0 || recurringCompliance.due_items?.length > 0) && (
              <div className="space-y-2">
                <p className="text-sm font-medium text-text-muted">Top due/overdue items (open employee detail):</p>
                {recurringCompliance.overdue_items?.slice(0, 3).map((item, idx) => (
                  <Link 
                    key={`overdue-${idx}`}
                    to={`/portal/employees/${item.employee_id}?tab=recurring`}
                    className="flex items-center justify-between p-2 rounded-lg bg-red-50 hover:bg-red-100 transition-colors"
                    data-testid={`recurring-overdue-${idx}`}
                  >
                    <div className="flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4 text-red-600" />
                      <span className="text-sm text-red-800">{item.item_name}</span>
                      <span className="text-xs text-red-600">({item.employee_name})</span>
                    </div>
                    <span className="text-xs text-red-600">{Math.abs(item.days_value || 0)}d overdue</span>
                  </Link>
                ))}
                {recurringCompliance.due_items?.slice(0, 2).map((item, idx) => (
                  <Link 
                    key={`due-${idx}`}
                    to={`/portal/employees/${item.employee_id}?tab=recurring`}
                    className="flex items-center justify-between p-2 rounded-lg bg-amber-50 hover:bg-amber-100 transition-colors"
                    data-testid={`recurring-due-${idx}`}
                  >
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4 text-amber-600" />
                      <span className="text-sm text-amber-800">{item.item_name}</span>
                      <span className="text-xs text-amber-600">({item.employee_name})</span>
                    </div>
                    <span className="text-xs text-amber-600">Due in {item.days_value || 0}d</span>
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Recent Employees */}
      <div className="grid grid-cols-1 gap-6">
        <Card className="border-[#E4E8EB] shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="font-heading text-lg">Recent Employees</CardTitle>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setShowRecentEmployees((v) => !v)}
                data-testid="toggle-recent-employees"
              >
                {showRecentEmployees ? 'Collapse' : 'Expand'}
                {showRecentEmployees ? <ChevronUp className="ml-1 h-4 w-4" /> : <ChevronDown className="ml-1 h-4 w-4" />}
              </Button>
              <Link to="/portal/employees">
                <Button variant="ghost" size="sm" className="text-primary" data-testid="view-all-employees">
                  View all
                  <ArrowRight className="ml-1 h-4 w-4" />
                </Button>
              </Link>
            </div>
          </CardHeader>
          <CardContent>
            {employees.length === 0 ? (
              <div className="text-center py-8 text-text-muted">
                <Users className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>No employees yet</p>
                <Link to="/portal/recruitment">
                  <Button className="mt-4 bg-primary hover:bg-primary-hover text-white rounded-xl">
                    Invite your first applicant
                  </Button>
                </Link>
              </div>
            ) : showRecentEmployees ? (
              <div className="space-y-3">
                {employees.slice(0, 5).map((emp) => {
                  const readiness = emp.canonical_readiness || {};
                  const isReady = readiness.is_work_ready === true;
                  const isSupervisedStart = readiness.can_promote === true && readiness.is_work_ready !== true;
                  const notReadyReason = readiness.blockers?.[0] || readiness.blocker_details?.[0]?.reason;
                  
                  return (
                    <Link
                      key={emp.id}
                      to={`/portal/employees/${emp.id}`}
                      className="flex items-center justify-between p-3 rounded-xl hover:bg-[#F8FAFA] transition-colors"
                      data-testid={`employee-row-${emp.id}`}
                    >
                      <div className="flex items-center gap-3">
                        <EmployeeAvatar
                          employeeId={emp.id}
                          firstName={emp.first_name}
                          lastName={emp.last_name}
                          hasPhoto={!!emp.profile_photo_url}
                          token={token}
                          size="md"
                        />
                        <div>
                          <p className="font-medium text-text-primary">{emp.first_name} {emp.last_name}</p>
                          <p className="text-sm text-text-muted">{emp.role}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-right hidden sm:block">
                          <p className="text-sm font-medium text-text-primary">{readiness.overall_percentage ?? 0}%</p>
                          <p className="text-xs text-text-muted">Readiness</p>
                        </div>
                        <div className="text-right">
                          <span 
                            className={`px-2 py-1 rounded-lg text-xs font-medium ${
                              isReady ? 'bg-green-100 text-green-700' :
                              isSupervisedStart ? 'bg-amber-100 text-amber-700' :
                              'bg-red-100 text-red-700'
                            }`}
                            title={notReadyReason || (isReady ? 'Canonical unified readiness complete' : 'Canonical unified readiness blockers remain')}
                          >
                            {isReady ? 'Ready for Work' : isSupervisedStart ? 'Promotion Eligible' : 'Not ready for work'}
                          </span>
                          {/* UI INTEGRITY: Show WHY someone is Not Ready */}
                          {!isReady && !isSupervisedStart && notReadyReason && (
                            <p className="text-[10px] text-red-600 mt-0.5 max-w-[120px] truncate" title={notReadyReason}>
                              {notReadyReason}
                            </p>
                          )}
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            ) : (
              <p className="text-sm text-text-muted">
                Employee list collapsed to reduce dashboard noise. Use Expand to review latest staff statuses.
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
